// Phase — Reports Dashboard. Multi-series bar chart (e.g. Purchase vs Sales by
// Vendor) — CategoryBarChart is single-series only, so this is a sibling for
// the grouped-bars shape, following the same axis/tooltip styling.
import * as React from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export interface GroupedBarDatum {
  label: string;
  [seriesKey: string]: string | number;
}

export interface GroupedBarSeries {
  key: string;
  label: string;
  color: string;
}

export interface GroupedBarChartProps {
  data: GroupedBarDatum[];
  series: GroupedBarSeries[];
  valueFormatter?: (v: number) => string;
  layout?: "horizontal" | "vertical";
}

export function GroupedBarChart({ data, series, valueFormatter, layout = "horizontal" }: GroupedBarChartProps) {
  const fmt = valueFormatter ?? ((v: number) => String(v));
  const isVertical = layout === "vertical";
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={isVertical ? "vertical" : "horizontal"}
        margin={{ top: 8, right: 8, left: isVertical ? 8 : 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={!isVertical} vertical={isVertical} />
        {isVertical ? (
          <>
            <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
          </>
        ) : (
          <>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={56} />
          </>
        )}
        <Tooltip
          formatter={(v: number) => fmt(v)}
          contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {series.map((s) => (
          <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 4, 4]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default GroupedBarChart;
